/**
 * SECCIÓN P — Módulo Mapa Interactivo expandido.
 * Mantiene la funcionalidad existente: plano base via FileUploader, clic para
 * agregar pin, lista de áreas en panel izquierdo, botón Exportar PDF.
 * Agrega: múltiples mapas (P1), modelo de área expandido (P2-P4),
 * sistema de pins por disciplina (P3), tooltip al hover (P6), zonas
 * circulares y poligonales (P7), heat map + slider opacidad (P8),
 * reporte PDF de calor (P10).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Modal, FileUploader } from "../components/ui";
import PDFButton from "../components/PDFButton";
import {
  MapPin, Plus, X, Edit2, ChevronDown, ChevronRight, Trash2, Download,
  Flame, Crosshair, AlertTriangle,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────
// Tabla de pins por disciplina (P3)
// ─────────────────────────────────────────────────────────────────
const PIN_TABLE = {
  seguridad: [
    { tipo: "combustible", emoji: "🔥", label: "Combustible" },
    { tipo: "electrico", emoji: "⚡", label: "Eléctrico" },
    { tipo: "quimico_seg", emoji: "⚗", label: "Químico" },
    { tipo: "riesgo_especial", emoji: "⚠", label: "Riesgo Especial" },
    { tipo: "acceso_restringido", emoji: "🚫", label: "Acceso Restringido" },
    { tipo: "administrativo", emoji: "🏢", label: "Área Administrativa" },
    { tipo: "trabajos_altura", emoji: "🚧", label: "Trabajos en Altura" },
    { tipo: "maquinaria", emoji: "⚙", label: "Maquinaria / Equipo" },
  ],
  medio_ambiente: [
    { tipo: "fuente_agua", emoji: "💧", label: "Fuente de Agua / Pozo" },
    { tipo: "cuerpo_agua", emoji: "🌊", label: "Cuerpo de Agua" },
    { tipo: "flora", emoji: "🌱", label: "Flora / Vegetación Sensible" },
    { tipo: "fauna", emoji: "🦋", label: "Fauna / Biodiversidad" },
    { tipo: "emision_atm", emoji: "💨", label: "Punto de Emisión Atmosférica" },
    { tipo: "punto_descarga", emoji: "🏭", label: "Punto de Descarga" },
    { tipo: "area_residuos", emoji: "♻", label: "Área de Residuos" },
    { tipo: "monitoreo_amb", emoji: "🌡", label: "Punto de Monitoreo Ambiental" },
    { tipo: "remediacion", emoji: "🌍", label: "Área de Remediación" },
  ],
  salud_ocupacional: [
    { tipo: "atencion_medica", emoji: "🏥", label: "Punto de Atención Médica" },
    { tipo: "botiquin", emoji: "💊", label: "Botiquín / Farmacia" },
    { tipo: "exposicion_quim", emoji: "🫁", label: "Exposición Química" },
    { tipo: "exposicion_ruido", emoji: "👂", label: "Exposición a Ruido" },
    { tipo: "calor_radiacion", emoji: "☀", label: "Calor / Radiación" },
    { tipo: "agente_biologico", emoji: "🧪", label: "Agente Biológico" },
    { tipo: "ergonomia", emoji: "💪", label: "Ergonomía / Sobreesfuerzo" },
  ],
  manual: [{ tipo: "manual", emoji: "✏", label: "Definido por usuario" }],
};

const RISK_COLORS = {
  ALTO: "#D50000",
  MEDIO: "#FF6D00",
  BAJO: "#FFD600",
  MONITOREO: "#1565C0",
};

const MAP_TYPE_ICON = {
  seguridad: "🛡",
  medio_ambiente: "🌿",
  salud_ocupacional: "🏥",
  mixto: "🗺",
  custom: "✏",
};

const MAP_TYPE_LABEL = {
  seguridad: "Seguridad",
  medio_ambiente: "Medio Ambiente",
  salud_ocupacional: "Salud Ocupacional",
  mixto: "Mixto",
  custom: "Personalizado",
};

const TAB_COLORS = [
  "#1565C0", "#D50000", "#2E7D32", "#E65100",
  "#6A1B9A", "#00695C", "#546E7A", "#212121",
];

const STATUS_AREA = ["Operativa", "En Mantenimiento", "Restringida", "Cerrada"];
const TIPO_RECEPTOR = ["Suelo", "Agua Superficial", "Agua Subterránea", "Aire", "Flora", "Fauna", "Múltiple"];
const FREC_MONITOREO = ["Diario", "Semanal", "Mensual", "Trimestral", "Anual"];
const AGENTES_EXP = ["Químico", "Físico", "Biológico", "Ergonómico", "Psicosocial"];
const NORMAS_SO = ["NOM-010-STPS", "NOM-011-STPS", "NOM-047-SSA1", "Otra"];
const FREC_VIG = ["Mensual", "Trimestral", "Semestral", "Anual"];

function findPin(pin_tipo) {
  if (!pin_tipo) return null;
  for (const list of Object.values(PIN_TABLE)) {
    const m = list.find((p) => p.tipo === pin_tipo);
    if (m) return m;
  }
  return null;
}

function pinEmoji(area) {
  if (area.pin_tipo === "manual" && area.pin_emoji_manual) return area.pin_emoji_manual;
  const p = findPin(area.pin_tipo);
  return p ? p.emoji : "📍";
}

function areaPinColor(area, heatMap) {
  if (heatMap && area.heat_color) return area.heat_color;
  if (area.pin_tipo === "manual" && area.pin_color_manual) return area.pin_color_manual;
  return RISK_COLORS[area.risk] || RISK_COLORS.BAJO;
}

function riskBadgeColor(r) {
  return RISK_COLORS[r] || RISK_COLORS.BAJO;
}

// ─────────────────────────────────────────────────────────────────
const LS_ACTIVE_MAP_KEY = "xerome_activeMapId";
const lsGetActiveMap = () => {
  try { return localStorage.getItem(LS_ACTIVE_MAP_KEY) || null; } catch { return null; }
};
const lsSetActiveMap = (mapId) => {
  try {
    if (mapId) localStorage.setItem(LS_ACTIVE_MAP_KEY, mapId);
    else localStorage.removeItem(LS_ACTIVE_MAP_KEY);
  } catch (_) {}
};

export default function MapModule() {
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [config, setConfig] = useState(null);
  const [areas, setAreas] = useState([]);
  const [prefs, setPrefs] = useState({ map_opacity: 0.35, heat_map_active: false });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingPoint, setPendingPoint] = useState(null); // {x,y} for new area
  const [showNewMap, setShowNewMap] = useState(false);
  const [tooltipArea, setTooltipArea] = useState(null); // for hover details
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [polyDraw, setPolyDraw] = useState(null); // { area_id, vertices: [{x,y}] }
  const imgRef = useRef(null);

  // Cambia la pestaña activa de forma estable + persiste en localStorage.
  // Evita reasignaciones que solo cambian la referencia del objeto.
  const changeActiveTab = useCallback((tab) => {
    if (!tab) {
      setActiveTab(null);
      lsSetActiveMap(null);
      return;
    }
    setActiveTab((prev) => (prev && prev.id === tab.id ? prev : tab));
    lsSetActiveMap(tab.id);
  }, []);

  // reloadTabs SIN dependencia de activeTab para evitar loops de re-render.
  // Usa el setter funcional para reconciliar el tab activo con la lista fresca.
  const reloadTabs = useCallback(async () => {
    const { data } = await api.get("/map/tabs");
    const list = data || [];
    setTabs(list);
    setActiveTab((prev) => {
      if (list.length === 0) { lsSetActiveMap(null); return null; }
      // 1) Si hay prev y sigue existiendo → conservar la misma referencia
      if (prev) {
        const fresh = list.find((t) => t.id === prev.id);
        if (fresh) return prev; // misma referencia para no disparar efectos
      }
      // 2) Restaurar el guardado en localStorage si existe en la lista
      const savedId = lsGetActiveMap();
      const restored = savedId ? list.find((t) => t.id === savedId) : null;
      if (restored) { lsSetActiveMap(restored.id); return restored; }
      // 3) Fallback: primer tab
      lsSetActiveMap(list[0].id);
      return list[0];
    });
  }, []);

  const reloadConfig = useCallback(async () => {
    const { data: cfgs } = await api.get("/data/map_config");
    setConfig((cfgs || [])[0] || null);
  }, []);

  const reloadAreas = useCallback(async (mapId) => {
    if (!mapId) { setAreas([]); return; }
    const { data } = await api.get(`/map/areas?map_id=${encodeURIComponent(mapId)}`);
    setAreas(data || []);
  }, []);

  const reloadPrefs = useCallback(async (mapId) => {
    if (!mapId) return;
    try {
      const { data } = await api.get(`/map/user-preferences?map_id=${encodeURIComponent(mapId)}`);
      setPrefs(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { reloadTabs(); reloadConfig(); }, [reloadTabs, reloadConfig]);
  useEffect(() => {
    if (activeTab?.id) {
      reloadAreas(activeTab.id);
      reloadPrefs(activeTab.id);
    }
  }, [activeTab?.id, reloadAreas, reloadPrefs]);

  async function handlePlan(uploaded) {
    if (config) await api.patch(`/data/map_config/${config.id}`, { plan_file_id: uploaded.id });
    else await api.post("/data/map_config", { plan_file_id: uploaded.id });
    reloadConfig();
  }

  function handleMapClick(e) {
    if (!imgRef.current || !activeTab) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width * 100);
    const y = ((e.clientY - rect.top) / rect.height * 100);
    if (polyDraw) {
      // Add vertex
      setPolyDraw({ ...polyDraw, vertices: [...polyDraw.vertices, { x, y }] });
      return;
    }
    setEditing(null);
    setPendingPoint({ x: Number(x.toFixed(2)), y: Number(y.toFixed(2)) });
    setShowForm(true);
  }

  async function saveArea(form) {
    const payload = { ...form };
    if (!editing && pendingPoint) {
      payload.x = pendingPoint.x;
      payload.y = pendingPoint.y;
    }
    payload.map_id = activeTab.id;
    if (editing) {
      await api.patch(`/data/map_areas/${editing.id}`, payload);
    } else {
      await api.post("/data/map_areas", payload);
    }
    setShowForm(false);
    setPendingPoint(null);
    setEditing(null);
    reloadAreas(activeTab.id);
  }

  async function deleteArea(id) {
    if (!window.confirm("¿Eliminar área?")) return;
    await api.delete(`/data/map_areas/${id}`);
    reloadAreas(activeTab.id);
  }

  async function setOpacity(v) {
    setPrefs((s) => ({ ...s, map_opacity: v }));
    try {
      await api.put("/map/user-preferences/opacity", { opacity: v, map_id: activeTab.id });
    } catch (e) { console.error(e); }
  }

  async function toggleHeatMap() {
    const next = !prefs.heat_map_active;
    setPrefs((s) => ({ ...s, heat_map_active: next }));
    try {
      await api.put("/map/user-preferences/heat-toggle", { active: next, map_id: activeTab.id });
    } catch (e) { console.error(e); }
  }

  async function downloadHeatPdf() {
    try {
      const res = await api.post(
        `/map/areas/heat-export/pdf?map_id=${encodeURIComponent(activeTab.id)}`,
        {}, { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `heat_${activeTab.name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Error PDF calor: " + (e?.response?.data?.detail || e.message));
    }
  }

  async function centerView() {
    // Simple: scroll the map into view (the image is already centered)
    imgRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  async function startPolyDrawing(area) {
    setPolyDraw({ area_id: area.id, vertices: area.zona_poligono || [] });
  }
  async function finishPolyDrawing() {
    if (!polyDraw || polyDraw.vertices.length < 3) {
      alert("Necesitas al menos 3 vértices.");
      return;
    }
    try {
      await api.patch(`/data/map_areas/${polyDraw.area_id}`, {
        zona_poligono: polyDraw.vertices, zona_tipo: "poligonal",
      });
      setPolyDraw(null);
      reloadAreas(activeTab.id);
    } catch (e) {
      alert("Error guardando polígono: " + (e?.response?.data?.detail || e.message));
    }
  }

  const planUrl = config?.plan_file_id
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/view/${config.plan_file_id}`
    : null;

  // Bug fix: <img src> no envía Authorization, así que /api/files/view/{id}
  // responde 401 y la imagen queda rota. Cargamos la imagen via axios (que sí
  // envía el Bearer token gracias al interceptor) y la servimos como blob URL.
  const [planBlobUrl, setPlanBlobUrl] = useState(null);
  useEffect(() => {
    let revoked = false;
    let currentUrl = null;
    async function load() {
      if (!config?.plan_file_id) { setPlanBlobUrl(null); return; }
      try {
        const res = await api.get(`/files/view/${config.plan_file_id}`, { responseType: "blob" });
        if (revoked) return;
        currentUrl = URL.createObjectURL(res.data);
        setPlanBlobUrl(currentUrl);
      } catch (e) {
        console.error("Error cargando plano:", e);
        setPlanBlobUrl(null);
      }
    }
    load();
    return () => {
      revoked = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [config?.plan_file_id]);

  // KPIs
  const counts = { ALTO: 0, MEDIO: 0, BAJO: 0, MONITOREO: 0 };
  let criticas = 0;
  let excedeCount = 0;
  for (const a of areas) {
    if (counts[a.risk] !== undefined) counts[a.risk]++;
    if ((a.heat_score || 0) > 75) criticas++;
    if (a.excede_lmp || a.excede_limite_exposicion) excedeCount++;
  }

  return (
    <div data-testid="map-module">
      <div className="flex items-center justify-between mb-3">
        <h1 className="card-h1" style={{ margin: 0 }}>Mapa Interactivo de la Instalación</h1>
        <div className="flex gap-2">
          <PDFButton endpoint="/pdf/module/map_areas" filename="mapa.pdf" label="Exportar PDF" />
        </div>
      </div>

      {/* P1 — Barra de pestañas */}
      <MapTabBar
        tabs={tabs} activeTab={activeTab}
        onChange={changeActiveTab}
        onNewMap={() => setShowNewMap(true)}
        onDeleteTab={async (tab) => {
          if (!window.confirm(`¿Eliminar el mapa "${tab.name}"?` + (tab.area_count > 0 ? ` Tiene ${tab.area_count} áreas que también se eliminarán.` : ""))) return;
          try {
            await api.delete(`/map/tabs/${tab.id}?confirm=true`);
            reloadTabs();
          } catch (e) {
            alert(e?.response?.data?.detail || e.message);
          }
        }}
        onReorder={async (newOrder) => {
          try {
            await api.put("/map/tabs/reorder", newOrder.map((t, i) => ({ map_id: t.id, order: i + 1 })));
            reloadTabs();
          } catch (e) { console.error(e); }
        }}
      />

      <div className="grid" style={{ gridTemplateColumns: "300px 1fr", gap: 16 }}>
        <div className="card" data-testid="map-side-panel">
          <div className="card-title">Áreas registradas</div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="kpi-card danger" data-testid="map-kpi-alto"><div className="label">Alto</div><div className="value text-lg">{counts.ALTO}</div></div>
            <div className="kpi-card warn" data-testid="map-kpi-medio"><div className="label">Medio</div><div className="value text-lg">{counts.MEDIO}</div></div>
            <div className="kpi-card" data-testid="map-kpi-bajo"><div className="label">Bajo</div><div className="value text-lg">{counts.BAJO}</div></div>
            <div className="kpi-card" data-testid="map-kpi-monitoreo"><div className="label">Monitoreo</div><div className="value text-lg">{counts.MONITOREO}</div></div>
            <div className="kpi-card danger" data-testid="map-kpi-criticas" style={{ gridColumn: "span 2" }}>
              <div className="label">Críticas (score &gt; 75)</div><div className="value text-lg">{criticas}</div>
            </div>
          </div>
          {excedeCount > 0 && (
            <div className="card mb-2" style={{ background: "rgba(213,0,0,0.10)", borderColor: "#D50000", padding: 8 }} data-testid="map-alert-excede">
              <AlertTriangle size={12} style={{ display: "inline", marginRight: 4, color: "#D50000" }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: "#D50000" }}>
                {excedeCount} área(s) exceden límite ambiental/SO
              </span>
            </div>
          )}
          <div className="grid gap-2" style={{ maxHeight: 500, overflowY: "auto" }}>
            {areas.map((a) => (
              <AreaCard
                key={a.id} area={a}
                onEdit={() => { setEditing(a); setShowForm(true); }}
                onDelete={() => deleteArea(a.id)}
              />
            ))}
            {areas.length === 0 && <div className="text-dim text-sm">Sin áreas en este mapa.</div>}
          </div>
        </div>

        <div className="card" style={{ position: "relative" }}>
          {!planUrl ? (
            <div>
              <div className="text-dim mb-3">Carga el plano base de la instalación (JPG/PNG/PDF):</div>
              <FileUploader module="map_config" onUploaded={handlePlan} />
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                <div className="text-sm text-dim">
                  {polyDraw ? "Haz clic para agregar vértices. Mín. 3." : "Haz clic en el plano para agregar un pin"}
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  <button className="btn btn-sm" onClick={centerView} title="Centrar vista" data-testid="map-center">
                    <Crosshair size={12} />
                  </button>
                  <button
                    className={`btn btn-sm ${prefs.heat_map_active ? "btn-primary" : ""}`}
                    onClick={toggleHeatMap}
                    data-testid="map-heat-toggle"
                  >
                    <Flame size={12} /> Mapa de Calor
                  </button>
                  <div className="flex items-center gap-1" style={{ fontSize: 11 }}>
                    <span className="text-dim">Opacidad</span>
                    <input
                      type="range" min={0} max={70} step={1}
                      value={Math.round((prefs.map_opacity || 0) * 100)}
                      onChange={(e) => setOpacity(parseFloat(e.target.value) / 100)}
                      data-testid="map-opacity-slider"
                    />
                    <span style={{ minWidth: 28 }}>{Math.round((prefs.map_opacity || 0) * 100)}%</span>
                  </div>
                  <FileUploader module="map_config" onUploaded={handlePlan} />
                </div>
              </div>

              {polyDraw && (
                <div className="card mb-2" style={{ background: "rgba(21,101,192,0.10)", padding: 8 }} data-testid="map-poly-bar">
                  Polígono en edición · {polyDraw.vertices.length} vértices
                  <button className="btn btn-sm btn-primary ml-2" onClick={finishPolyDrawing}>Cerrar polígono</button>
                  <button className="btn btn-sm ml-1" onClick={() => setPolyDraw(null)}>Cancelar</button>
                </div>
              )}

              <div style={{ position: "relative", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                <img
                  ref={imgRef} src={planBlobUrl || undefined} alt="Plano"
                  style={{ width: "100%", display: "block", cursor: polyDraw ? "crosshair" : "crosshair" }}
                  onClick={handleMapClick}
                  data-testid="map-plan-img"
                />

                {/* SVG overlay for zones */}
                <svg
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
                  preserveAspectRatio="none"
                  viewBox="0 0 100 100"
                >
                  {areas.map((a) => {
                    if (a.zona_tipo === "circular" && a.zona_radio_metros) {
                      const color = areaPinColor(a, prefs.heat_map_active);
                      const op = a.opacidad_individual ?? prefs.map_opacity;
                      // approx: radius in % of width (10m ≈ 1.5%)
                      const r = Math.min(40, (a.zona_radio_metros || 0) * 0.15);
                      return (
                        <g key={`circ-${a.id}`} style={{ pointerEvents: "auto", cursor: "pointer" }}
                          onMouseEnter={(e) => { setTooltipArea(a); setTooltipPos({ x: e.clientX, y: e.clientY }); }}
                          onMouseLeave={() => setTooltipArea(null)}
                        >
                          <circle cx={a.x} cy={a.y} r={r}
                            fill={color} fillOpacity={op}
                            stroke={RISK_COLORS[a.risk] || color} strokeWidth={0.4} />
                        </g>
                      );
                    }
                    if (a.zona_tipo === "poligonal" && Array.isArray(a.zona_poligono) && a.zona_poligono.length >= 3) {
                      const color = areaPinColor(a, prefs.heat_map_active);
                      const op = a.opacidad_individual ?? prefs.map_opacity;
                      const pts = a.zona_poligono.map((v) => `${v.x},${v.y}`).join(" ");
                      return (
                        <g key={`poly-${a.id}`} style={{ pointerEvents: "auto", cursor: "pointer" }}
                          onMouseEnter={(e) => { setTooltipArea(a); setTooltipPos({ x: e.clientX, y: e.clientY }); }}
                          onMouseLeave={() => setTooltipArea(null)}
                        >
                          <polygon points={pts}
                            fill={color} fillOpacity={op}
                            stroke={RISK_COLORS[a.risk] || color} strokeWidth={0.4} />
                        </g>
                      );
                    }
                    return null;
                  })}
                  {polyDraw && polyDraw.vertices.length >= 1 && (
                    <polyline
                      points={polyDraw.vertices.map((v) => `${v.x},${v.y}`).join(" ")}
                      fill="none" stroke="#1565C0" strokeWidth={0.5}
                    />
                  )}
                </svg>

                {/* Pins */}
                {areas.map((a) => {
                  const color = areaPinColor(a, prefs.heat_map_active);
                  return (
                    <div
                      key={a.id}
                      onClick={(e) => { e.stopPropagation(); setEditing(a); setShowForm(true); }}
                      onMouseEnter={(e) => { setTooltipArea(a); setTooltipPos({ x: e.clientX, y: e.clientY }); }}
                      onMouseLeave={() => setTimeout(() => setTooltipArea((t) => (t?.id === a.id ? null : t)), 80)}
                      title={a.name}
                      style={{
                        position: "absolute", left: `${a.x}%`, top: `${a.y}%`,
                        transform: "translate(-50%, -100%)", cursor: "pointer",
                        fontSize: 22, lineHeight: 1, filter: `drop-shadow(0 0 2px ${color})`,
                        userSelect: "none",
                      }}
                      data-testid={`map-pin-${a.id}`}
                    >
                      <span style={{ display: "inline-block", padding: "2px 4px", borderRadius: 4, background: `${color}cc`, color: "white" }}>
                        {pinEmoji(a)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* P8 — Leyenda de calor */}
              {prefs.heat_map_active && (
                <HeatLegend mapType={activeTab?.map_type || "mixto"} onExport={downloadHeatPdf} />
              )}

              {/* P6 — Tooltip */}
              {tooltipArea && (
                <AreaTooltip
                  area={tooltipArea}
                  position={tooltipPos}
                  onEdit={() => { setEditing(tooltipArea); setShowForm(true); setTooltipArea(null); }}
                  onClose={() => setTooltipArea(null)}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* P4 — Modal nueva/editar área */}
      <Modal
        open={showForm}
        onClose={() => { setShowForm(false); setEditing(null); setPendingPoint(null); }}
        title={editing ? `Editar área — ${editing.code || editing.name}` : "Nueva área"}
        size="xl"
        testId="map-area-form"
      >
        {showForm && (
          <AreaForm
            initial={editing}
            mapType={activeTab?.map_type || "mixto"}
            onCancel={() => { setShowForm(false); setEditing(null); setPendingPoint(null); }}
            onSave={saveArea}
            onStartPolyDraw={editing ? () => { setShowForm(false); startPolyDrawing(editing); } : null}
          />
        )}
      </Modal>

      {/* Nuevo mapa */}
      <Modal open={showNewMap} onClose={() => setShowNewMap(false)} title="Nuevo Mapa" size="md" testId="map-new-modal">
        <NewMapForm
          onCancel={() => setShowNewMap(false)}
          onCreated={async (created) => {
            setShowNewMap(false);
            await reloadTabs();
            changeActiveTab(created);
          }}
        />
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function MapTabBar({ tabs, activeTab, onChange, onNewMap, onDeleteTab, onReorder }) {
  const [dragIdx, setDragIdx] = useState(null);
  return (
    <div className="flex items-center gap-1 mb-3" style={{ overflowX: "auto", borderBottom: "1px solid var(--border-2)", paddingBottom: 4 }} data-testid="map-tab-bar">
      {tabs.map((t, i) => {
        const isActive = activeTab?.id === t.id;
        return (
          <div
            key={t.id}
            className="flex items-center gap-1"
            style={{
              padding: "6px 10px",
              borderBottom: isActive ? `3px solid ${t.tab_color}` : "3px solid transparent",
              background: isActive ? `${t.tab_color}1a` : "transparent",
              borderRadius: "6px 6px 0 0",
              cursor: "pointer", whiteSpace: "nowrap", userSelect: "none",
            }}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              if (dragIdx === null || dragIdx === i) return;
              const next = [...tabs];
              const [m] = next.splice(dragIdx, 1);
              next.splice(i, 0, m);
              setDragIdx(null);
              onReorder(next);
            }}
            onClick={() => onChange(t)}
            data-testid={`map-tab-${t.id}`}
          >
            <span style={{ fontSize: 14 }}>{MAP_TYPE_ICON[t.map_type] || "🗺"}</span>
            <span style={{ fontSize: 12, fontWeight: isActive ? 600 : 400 }}>{t.name}</span>
            {t.area_count > 0 && (
              <span style={{ fontSize: 10, background: "var(--bg-2)", padding: "0 5px", borderRadius: 8 }}>{t.area_count}</span>
            )}
            <button
              className="btn btn-sm"
              style={{ padding: 2, minWidth: 0, background: "transparent" }}
              onClick={(e) => { e.stopPropagation(); onDeleteTab(t); }}
              data-testid={`map-tab-delete-${t.id}`}
            ><X size={10} /></button>
          </div>
        );
      })}
      <button className="btn btn-sm" onClick={onNewMap} data-testid="map-tab-new"><Plus size={12} /> Nuevo mapa</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function NewMapForm({ onCancel, onCreated }) {
  const [name, setName] = useState("");
  const [mapType, setMapType] = useState("mixto");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#1565C0");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) { alert("Nombre requerido"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/map/tabs", { name: name.trim(), map_type: mapType, description, tab_color: color });
      onCreated(data);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-3" data-testid="map-new-form">
      <div>
        <label className="label">Nombre *</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="map-new-name" />
      </div>
      <div>
        <label className="label">Tipo de mapa</label>
        <div className="grid grid-cols-3 gap-2">
          {Object.keys(MAP_TYPE_ICON).map((k) => (
            <button
              key={k}
              className={`btn ${mapType === k ? "btn-primary" : ""}`}
              style={{ padding: "10px", flexDirection: "column", display: "flex", gap: 4, alignItems: "center" }}
              onClick={() => setMapType(k)}
              data-testid={`map-new-type-${k}`}
            >
              <span style={{ fontSize: 22 }}>{MAP_TYPE_ICON[k]}</span>
              <span style={{ fontSize: 11 }}>{MAP_TYPE_LABEL[k]}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="label">Descripción</label>
        <textarea className="textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div>
        <label className="label">Color de pestaña</label>
        <div className="flex gap-1 flex-wrap">
          {TAB_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              style={{
                width: 28, height: 28, borderRadius: 4, border: color === c ? "2px solid white" : "1px solid var(--border-2)",
                background: c, cursor: "pointer",
              }}
              data-testid={`map-new-color-${c.replace("#", "")}`}
            />
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button className="btn" onClick={onCancel} disabled={saving}>Cancelar</button>
        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="map-new-save">Crear Mapa</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function AreaCard({ area, onEdit, onDelete }) {
  const disciplinas = Array.isArray(area.disciplina) ? area.disciplina : (area.disciplina ? [area.disciplina] : []);
  const heatBarW = Math.min(100, Math.max(0, area.heat_score || 0));
  return (
    <div className="p-2 rounded" style={{ background: "var(--bg-2)" }} data-testid={`map-area-card-${area.id}`}>
      <div className="flex items-center justify-between gap-1">
        <div className="flex-1" style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: riskBadgeColor(area.risk) }}>
            {area.code || "—"} — {area.name}
          </div>
          <div className="flex items-center gap-1 flex-wrap" style={{ fontSize: 10 }}>
            <span>{pinEmoji(area)}</span>
            {area.category && <span style={{ background: "var(--bg-3,#1a1a1a)", padding: "0 4px", borderRadius: 3 }}>{area.category}</span>}
            {area.status_area && <span style={{ background: "var(--bg-3,#1a1a1a)", padding: "0 4px", borderRadius: 3 }}>{area.status_area}</span>}
          </div>
          <div className="flex gap-1 mt-1">
            {disciplinas.includes("seguridad") && <span style={{ fontSize: 10 }}>🛡</span>}
            {disciplinas.includes("medio_ambiente") && <span style={{ fontSize: 10 }}>🌿</span>}
            {disciplinas.includes("salud_ocupacional") && <span style={{ fontSize: 10 }}>🏥</span>}
            {disciplinas.includes("manual") && <span style={{ fontSize: 10 }}>✏</span>}
          </div>
        </div>
        <div className="flex gap-1">
          <button className="btn btn-sm" onClick={onEdit} data-testid={`map-area-edit-${area.id}`}><Edit2 size={10} /></button>
          <button className="btn btn-sm btn-danger" onClick={onDelete} data-testid={`map-area-delete-${area.id}`}><Trash2 size={10} /></button>
        </div>
      </div>
      <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 3 }}>
        {area.supervisor_hse && <div>HSE: {area.supervisor_hse}</div>}
        {area.responsable_operativo && <div>Resp: {area.responsable_operativo}</div>}
      </div>
      <div style={{ marginTop: 4 }}>
        <div style={{ background: "var(--bg-3,#1a1a1a)", height: 4, borderRadius: 2, overflow: "hidden" }}>
          <div style={{ width: `${heatBarW}%`, height: "100%", background: area.heat_color || "#00C853" }} />
        </div>
        <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 2 }}>
          Score: {area.heat_score || 0}/100
        </div>
      </div>
      <div className="flex gap-2 flex-wrap" style={{ fontSize: 10, marginTop: 2 }}>
        <span>Inspección: {area.dias_desde_inspeccion ?? "—"} d</span>
        <span>Último evento: {area.dias_desde_evento ?? "—"} d</span>
        {area.excede_lmp && <span style={{ color: "#D50000", fontWeight: 600 }}>⚠ Excede LMP</span>}
        {area.excede_limite_exposicion && <span style={{ color: "#D50000", fontWeight: 600 }}>⚠ Excede SO</span>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function AreaTooltip({ area, position, onEdit, onClose }) {
  const disciplinas = Array.isArray(area.disciplina) ? area.disciplina : (area.disciplina ? [area.disciplina] : []);
  const env = area.env_data || {};
  const health = area.health_data || {};
  const heatBarW = Math.min(100, Math.max(0, area.heat_score || 0));
  return (
    <div
      style={{
        position: "fixed", left: Math.min(position.x + 10, window.innerWidth - 360),
        top: Math.min(position.y + 10, window.innerHeight - 400),
        background: "var(--bg-2,#1a1a1a)", border: "1px solid var(--border-2)",
        borderRadius: 6, padding: 10, width: 340, fontSize: 11,
        zIndex: 9999, boxShadow: "0 4px 16px rgba(0,0,0,0.6)",
      }}
      data-testid={`map-tooltip-${area.id}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-start mb-1">
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {pinEmoji(area)} {area.code || ""} — {area.name}
        </div>
        <button onClick={onClose} className="btn btn-sm" style={{ padding: 2 }}><X size={10} /></button>
      </div>
      <div className="flex gap-1 mb-2">
        <span style={{ background: riskBadgeColor(area.risk), color: "white", padding: "1px 5px", borderRadius: 3, fontSize: 10 }}>
          {area.risk || "—"}
        </span>
        {area.status_area && <span style={{ background: "var(--bg-3)", padding: "1px 5px", borderRadius: 3, fontSize: 10 }}>{area.status_area}</span>}
        {area.category && <span style={{ background: "var(--bg-3)", padding: "1px 5px", borderRadius: 3, fontSize: 10 }}>{area.category}</span>}
      </div>
      {(area.responsable_operativo || area.supervisor_hse) && (
        <div className="mb-2">
          {area.responsable_operativo && <div>Operativo: <b>{area.responsable_operativo}</b> {area.responsable_operativo_contacto ? `· ${area.responsable_operativo_contacto}` : ""}</div>}
          {area.supervisor_hse && <div>HSE/SSPA: <b>{area.supervisor_hse}</b> {area.supervisor_hse_contacto ? `· ${area.supervisor_hse_contacto}` : ""}</div>}
          {area.ubicacion_zona && <div>Ubicación: {area.ubicacion_zona}</div>}
          {area.superficie_m2 ? <div>Superficie: {area.superficie_m2} m²</div> : null}
        </div>
      )}
      <div className="mb-2">
        <div style={{ background: "var(--bg-3)", height: 4, borderRadius: 2, overflow: "hidden", marginBottom: 2 }}>
          <div style={{ width: `${heatBarW}%`, height: "100%", background: area.heat_color || "#00C853" }} />
        </div>
        <div>Score: <b>{area.heat_score || 0}/100</b></div>
        <div>Última inspección: {area.dias_desde_inspeccion != null ? `${area.dias_desde_inspeccion} días` : "Sin registro"}</div>
        <div>Último evento: {area.dias_desde_evento != null ? `${area.dias_desde_evento} días` : "Sin registro"}</div>
      </div>
      {disciplinas.includes("medio_ambiente") && (env.tipo_receptor || env.parametro_monitoreo) && (
        <div className="mb-2" style={{ borderTop: "1px solid var(--border-2)", paddingTop: 4 }}>
          <b>🌿 Medio Ambiente</b>
          {env.tipo_receptor && <div>Receptor: {env.tipo_receptor}</div>}
          {env.parametro_monitoreo && <div>Monitoreo: {env.parametro_monitoreo} — Último: {env.ultimo_valor_medido ?? "—"} {env.unidad_medida_env || ""}</div>}
          {env.limite_maximo_permisible != null && <div>LMP: {env.limite_maximo_permisible} {env.unidad_medida_env || ""} —
            {area.excede_lmp
              ? <span style={{ color: "#D50000", fontWeight: 600 }}> ⚠ EXCEDE</span>
              : <span style={{ color: "#00C853" }}> ✓ OK</span>}
          </div>}
        </div>
      )}
      {disciplinas.includes("salud_ocupacional") && (health.agente_exposicion || health.norma_aplicable_so) && (
        <div className="mb-2" style={{ borderTop: "1px solid var(--border-2)", paddingTop: 4 }}>
          <b>🏥 Salud Ocupacional</b>
          {health.agente_exposicion && <div>Agente: {health.agente_exposicion} — Norma: {health.norma_aplicable_so || "—"}</div>}
          {health.limite_exposicion_normativo != null && <div>Exp: {health.nivel_exposicion_actual ?? "—"} / Límite: {health.limite_exposicion_normativo} {health.unidad_exposicion || ""}
            {area.excede_limite_exposicion
              ? <span style={{ color: "#D50000", fontWeight: 600 }}> ⚠ EXCEDE</span>
              : <span style={{ color: "#00C853" }}> ✓ OK</span>}
          </div>}
          {Array.isArray(health.epp_requerido) && health.epp_requerido.length > 0 && <div>EPP: {health.epp_requerido.join(", ")}</div>}
        </div>
      )}
      {area.controls && (
        <div className="mb-2" style={{ borderTop: "1px solid var(--border-2)", paddingTop: 4 }}>
          <b>Controles:</b> {String(area.controls).slice(0, 120)}{area.controls.length > 120 ? "…" : ""}
        </div>
      )}
      <div className="flex justify-end gap-1 mt-2">
        <button className="btn btn-sm" onClick={onEdit} data-testid={`map-tooltip-edit-${area.id}`}><Edit2 size={10} /> Editar</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function HeatLegend({ mapType, onExport }) {
  let factors = "Factores: nivel de riesgo (40%) · días sin inspección (30%) · días desde último accidente (30%)";
  if (mapType === "medio_ambiente") factors = "Factores: nivel de riesgo (40%) · días sin monitoreo (30%) · ¿excede LMP? (30%)";
  if (mapType === "salud_ocupacional") factors = "Factores: nivel de riesgo (40%) · días sin inspección (30%) · ¿excede límite exposición? (30%)";
  return (
    <div
      style={{
        position: "absolute", right: 12, bottom: 12,
        background: "var(--bg-2,#1a1a1a)", border: "1px solid var(--border-2)",
        borderRadius: 8, padding: 10, fontSize: 11, width: 280, zIndex: 10,
      }}
      data-testid="map-heat-legend"
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Índice de Riesgo Operativo</div>
      <div className="flex items-center gap-2"><span style={{ background: "#00C853", width: 12, height: 12, borderRadius: 2, display: "inline-block" }} />0–25 Zona segura</div>
      <div className="flex items-center gap-2"><span style={{ background: "#FFD600", width: 12, height: 12, borderRadius: 2, display: "inline-block" }} />26–50 Atención requerida</div>
      <div className="flex items-center gap-2"><span style={{ background: "#FF6D00", width: 12, height: 12, borderRadius: 2, display: "inline-block" }} />51–75 Riesgo elevado</div>
      <div className="flex items-center gap-2"><span style={{ background: "#D50000", width: 12, height: 12, borderRadius: 2, display: "inline-block" }} />76–100 Zona crítica</div>
      <div style={{ marginTop: 6, color: "var(--text-faint)", fontSize: 10 }}>{factors}</div>
      <button className="btn btn-sm mt-2 w-full" onClick={onExport} data-testid="map-heat-export-pdf">
        <Download size={10} /> Exportar Reporte de Calor PDF
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
function Section({ title, defaultOpen = false, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: "1px solid var(--border-2)", paddingTop: 6, marginTop: 6 }} data-testid={testId}>
      <div
        onClick={() => setOpen(!open)}
        style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontWeight: 600, fontSize: 12 }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {title}
      </div>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

function AreaForm({ initial, mapType, onCancel, onSave, onStartPolyDraw }) {
  const [form, setForm] = useState(() => ({
    name: "", code: "", category: "", risk: "BAJO", status_area: "Operativa",
    disciplina: [], pin_tipo: "", pin_emoji_manual: "", pin_color_manual: "#1565C0", pin_nombre_manual: "",
    responsable_operativo: "", responsable_operativo_contacto: "",
    supervisor_hse: "", supervisor_hse_contacto: "",
    ubicacion_zona: "", superficie_m2: "",
    linked_module: "",
    zona_tipo: "punto", zona_radio_metros: 10, zona_poligono: [],
    opacidad_individual: null,
    fecha_ultima_inspeccion: "", fecha_ultimo_evento: "", descripcion_ultimo_evento: "",
    env_data: {}, health_data: {}, custom_fields: [],
    controls: "", notes: "",
    ...initial,
  }));
  const [saving, setSaving] = useState(false);
  const setF = (patch) => setForm((s) => ({ ...s, ...patch }));
  const setEnv = (patch) => setForm((s) => ({ ...s, env_data: { ...(s.env_data || {}), ...patch } }));
  const setHealth = (patch) => setForm((s) => ({ ...s, health_data: { ...(s.health_data || {}), ...patch } }));
  const toggleDisc = (d) => setF({ disciplina: form.disciplina.includes(d) ? form.disciplina.filter((x) => x !== d) : [...form.disciplina, d] });

  const includesMA = form.disciplina.includes("medio_ambiente");
  const includesSO = form.disciplina.includes("salud_ocupacional");
  const includesManual = form.disciplina.includes("manual");

  const env = form.env_data || {};
  const health = form.health_data || {};
  const excede_lmp_calc = env.ultimo_valor_medido != null && env.limite_maximo_permisible != null && env.limite_maximo_permisible !== ""
    && parseFloat(env.ultimo_valor_medido) > parseFloat(env.limite_maximo_permisible);
  const excede_exp_calc = health.nivel_exposicion_actual != null && health.limite_exposicion_normativo != null && health.limite_exposicion_normativo !== ""
    && parseFloat(health.nivel_exposicion_actual) > parseFloat(health.limite_exposicion_normativo);

  // Filter pins by selected disciplinas
  const availablePins = [];
  for (const d of form.disciplina) {
    if (PIN_TABLE[d]) availablePins.push(...PIN_TABLE[d].map((p) => ({ ...p, disciplina: d })));
  }

  async function save() {
    if (!form.name?.trim()) { alert("Nombre requerido"); return; }
    setSaving(true);
    try {
      await onSave(form);
    } finally { setSaving(false); }
  }

  function addCustomField() {
    if ((form.custom_fields || []).length >= 5) { alert("Máx. 5 campos"); return; }
    setF({ custom_fields: [...(form.custom_fields || []), { campo_nombre: "", campo_valor: "", campo_tipo: "texto" }] });
  }
  function removeCustomField(i) {
    setF({ custom_fields: (form.custom_fields || []).filter((_, idx) => idx !== i) });
  }

  return (
    <div className="space-y-2" data-testid="map-area-form-inner">
      <div className="grid grid-cols-2 gap-2">
        <div><label className="label">Nombre *</label><input className="input" value={form.name || ""} onChange={(e) => setF({ name: e.target.value })} data-testid="area-name" /></div>
        <div><label className="label">Código</label><input className="input" value={form.code || ""} onChange={(e) => setF({ code: e.target.value })} /></div>
        <div><label className="label">Categoría</label>
          <select className="select" value={form.category || ""} onChange={(e) => setF({ category: e.target.value })}>
            <option value="">--</option>
            <option>Almacenamiento sustancias/residuos</option>
            <option>Combustible</option><option>Agua</option><option>Área verde</option>
            <option>Procesos/Taller</option><option>Maniobras</option><option>Administrativo</option>
            <option>Equipos medición</option><option>Riesgo especial</option><option>Otro</option>
          </select>
        </div>
        <div><label className="label">Nivel de riesgo</label>
          <select className="select" value={form.risk || "BAJO"} onChange={(e) => setF({ risk: e.target.value })}>
            <option>ALTO</option><option>MEDIO</option><option>BAJO</option><option>MONITOREO</option>
          </select>
        </div>
        <div><label className="label">Status del área</label>
          <select className="select" value={form.status_area || "Operativa"} onChange={(e) => setF({ status_area: e.target.value })}>
            {STATUS_AREA.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <Section title="Disciplina y Pin" defaultOpen testId="map-form-sect-disc">
        <div className="grid grid-cols-4 gap-2 mb-2">
          {[
            ["seguridad", "🛡", "Seguridad"],
            ["medio_ambiente", "🌿", "Medio Ambiente"],
            ["salud_ocupacional", "🏥", "Salud Ocup."],
            ["manual", "✏", "Manual"],
          ].map(([k, icon, lbl]) => (
            <button
              key={k}
              type="button"
              className={`btn ${form.disciplina.includes(k) ? "btn-primary" : ""}`}
              onClick={() => toggleDisc(k)}
              data-testid={`area-disc-${k}`}
              style={{ padding: 8, fontSize: 11, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
            >
              <span style={{ fontSize: 20 }}>{icon}</span>{lbl}
            </button>
          ))}
        </div>
        {availablePins.length > 0 && (
          <div>
            <label className="label">Tipo de pin</label>
            <div className="grid grid-cols-4 gap-1" style={{ maxHeight: 200, overflowY: "auto" }}>
              {availablePins.map((p) => (
                <button
                  key={p.tipo}
                  type="button"
                  onClick={() => setF({ pin_tipo: p.tipo })}
                  className="btn btn-sm"
                  style={{
                    padding: 4, fontSize: 10, display: "flex", flexDirection: "column", alignItems: "center",
                    border: form.pin_tipo === p.tipo ? `2px solid ${RISK_COLORS[form.risk]}` : undefined,
                  }}
                  data-testid={`area-pin-${p.tipo}`}
                >
                  <span style={{ fontSize: 18 }}>{p.emoji}</span>{p.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {includesManual && (
          <div className="grid grid-cols-3 gap-2 mt-2">
            <div><label className="label">Nombre tipo manual</label><input className="input" value={form.pin_nombre_manual || ""} onChange={(e) => setF({ pin_nombre_manual: e.target.value })} /></div>
            <div><label className="label">Emoji</label><input className="input" value={form.pin_emoji_manual || ""} onChange={(e) => setF({ pin_emoji_manual: e.target.value })} placeholder="🚩" /></div>
            <div><label className="label">Color</label><input className="input" type="color" value={form.pin_color_manual || "#1565C0"} onChange={(e) => setF({ pin_color_manual: e.target.value })} /></div>
          </div>
        )}
      </Section>

      <Section title="Responsables" testId="map-form-sect-resp">
        <div className="grid grid-cols-2 gap-2">
          <div><label className="label">Responsable Operativo</label><input className="input" value={form.responsable_operativo || ""} onChange={(e) => setF({ responsable_operativo: e.target.value })} /></div>
          <div><label className="label">Contacto Resp.</label><input className="input" value={form.responsable_operativo_contacto || ""} onChange={(e) => setF({ responsable_operativo_contacto: e.target.value })} /></div>
          <div><label className="label">Supervisor HSE/SSPA</label><input className="input" value={form.supervisor_hse || ""} onChange={(e) => setF({ supervisor_hse: e.target.value })} /></div>
          <div><label className="label">Contacto HSE</label><input className="input" value={form.supervisor_hse_contacto || ""} onChange={(e) => setF({ supervisor_hse_contacto: e.target.value })} /></div>
        </div>
      </Section>

      <Section title="Ubicación y Superficie" testId="map-form-sect-ubi">
        <div className="grid grid-cols-3 gap-2">
          <div><label className="label">Ubicación/Zona</label><input className="input" value={form.ubicacion_zona || ""} onChange={(e) => setF({ ubicacion_zona: e.target.value })} /></div>
          <div><label className="label">Superficie (m²)</label><input className="input" type="number" value={form.superficie_m2 ?? ""} onChange={(e) => setF({ superficie_m2: e.target.value === "" ? "" : parseFloat(e.target.value) })} /></div>
          <div><label className="label">Módulo Vinculado</label><input className="input" value={form.linked_module || ""} onChange={(e) => setF({ linked_module: e.target.value })} /></div>
        </div>
      </Section>

      <Section title="Zona en el mapa" testId="map-form-sect-zona">
        <div className="flex gap-2 mb-2">
          {[["punto", "📍 Punto"], ["circular", "⭕ Circular"], ["poligonal", "🔷 Poligonal"]].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-1" style={{ fontSize: 11 }}>
              <input type="radio" checked={form.zona_tipo === k} onChange={() => setF({ zona_tipo: k })} data-testid={`area-zone-${k}`} /> {lbl}
            </label>
          ))}
        </div>
        {form.zona_tipo === "circular" && (
          <div><label className="label">Radio (m)</label><input className="input" type="number" value={form.zona_radio_metros ?? 10} onChange={(e) => setF({ zona_radio_metros: parseFloat(e.target.value || 0) })} /></div>
        )}
        {form.zona_tipo === "poligonal" && (
          <div className="text-dim" style={{ fontSize: 11 }}>
            Dibuja el polígono directamente en el mapa después de guardar. Haz clic en el mapa para definir los vértices del área.
            {initial && onStartPolyDraw && (
              <button className="btn btn-sm ml-2" type="button" onClick={onStartPolyDraw} data-testid="area-poly-start">Dibujar polígono</button>
            )}
            {Array.isArray(form.zona_poligono) && form.zona_poligono.length >= 3 && (
              <div className="mt-1">Polígono actual: {form.zona_poligono.length} vértices</div>
            )}
          </div>
        )}
        <div className="mt-2">
          <label className="flex items-center gap-1" style={{ fontSize: 11 }}>
            <input
              type="checkbox"
              checked={form.opacidad_individual == null}
              onChange={(e) => setF({ opacidad_individual: e.target.checked ? null : 0.35 })}
              data-testid="area-opacity-global"
            />
            Usar opacidad global del mapa
          </label>
          {form.opacidad_individual != null && (
            <div className="flex items-center gap-2">
              <input type="range" min={0} max={70} value={Math.round((form.opacidad_individual || 0) * 100)} onChange={(e) => setF({ opacidad_individual: parseFloat(e.target.value) / 100 })} data-testid="area-opacity-slider" />
              <span>{Math.round((form.opacidad_individual || 0) * 100)}%</span>
            </div>
          )}
        </div>
      </Section>

      <Section title="Historial de Seguridad" testId="map-form-sect-hist">
        <div className="grid grid-cols-3 gap-2">
          <div><label className="label">Fecha última inspección</label><input className="input" type="date" value={form.fecha_ultima_inspeccion ? String(form.fecha_ultima_inspeccion).slice(0, 10) : ""} onChange={(e) => setF({ fecha_ultima_inspeccion: e.target.value })} data-testid="area-fecha-insp" /></div>
          <div><label className="label">Fecha último evento</label><input className="input" type="date" value={form.fecha_ultimo_evento ? String(form.fecha_ultimo_evento).slice(0, 10) : ""} onChange={(e) => setF({ fecha_ultimo_evento: e.target.value })} data-testid="area-fecha-evt" /></div>
          <div style={{ gridColumn: "span 3" }}><label className="label">Descripción del evento</label><textarea className="textarea" rows={2} value={form.descripcion_ultimo_evento || ""} onChange={(e) => setF({ descripcion_ultimo_evento: e.target.value })} /></div>
        </div>
      </Section>

      {includesMA && (
        <Section title="Medio Ambiente" defaultOpen testId="map-form-sect-ma">
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">Tipo de receptor</label>
              <select className="select" value={env.tipo_receptor || ""} onChange={(e) => setEnv({ tipo_receptor: e.target.value })}>
                <option value="">--</option>{TIPO_RECEPTOR.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div><label className="label">Distancia cuerpo de agua (m)</label><input className="input" type="number" value={env.distancia_cuerpo_agua_m ?? ""} onChange={(e) => setEnv({ distancia_cuerpo_agua_m: e.target.value === "" ? "" : parseFloat(e.target.value) })} /></div>
            <div><label className="label">Permiso ambiental requerido</label>
              <select className="select" value={env.permiso_ambiental_requerido ? "Si" : "No"} onChange={(e) => setEnv({ permiso_ambiental_requerido: e.target.value === "Si" })}>
                <option>No</option><option>Si</option>
              </select>
            </div>
            {env.permiso_ambiental_requerido && (
              <div><label className="label">Número de permiso</label><input className="input" value={env.numero_permiso || ""} onChange={(e) => setEnv({ numero_permiso: e.target.value })} /></div>
            )}
            <div><label className="label">Parámetro de monitoreo</label><input className="input" value={env.parametro_monitoreo || ""} onChange={(e) => setEnv({ parametro_monitoreo: e.target.value })} /></div>
            <div><label className="label">Frecuencia</label>
              <select className="select" value={env.frecuencia_monitoreo || ""} onChange={(e) => setEnv({ frecuencia_monitoreo: e.target.value })}>
                <option value="">--</option>{FREC_MONITOREO.map((f) => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div><label className="label">Último valor medido</label><input className="input" type="number" value={env.ultimo_valor_medido ?? ""} onChange={(e) => setEnv({ ultimo_valor_medido: e.target.value === "" ? "" : parseFloat(e.target.value) })} data-testid="area-env-val" /></div>
            <div><label className="label">Unidad</label><input className="input" value={env.unidad_medida_env || ""} onChange={(e) => setEnv({ unidad_medida_env: e.target.value })} /></div>
            <div><label className="label">Límite Máximo Permisible (LMP)</label><input className="input" type="number" value={env.limite_maximo_permisible ?? ""} onChange={(e) => setEnv({ limite_maximo_permisible: e.target.value === "" ? "" : parseFloat(e.target.value) })} data-testid="area-env-lmp" /></div>
            <div>
              <label className="label">Indicador LMP</label>
              <div data-testid="area-env-lmp-badge">
                {env.ultimo_valor_medido == null || env.limite_maximo_permisible == null || env.limite_maximo_permisible === ""
                  ? <span className="badge" style={{ background: "var(--bg-3)" }}>Sin dato</span>
                  : excede_lmp_calc
                  ? <span className="badge" style={{ background: "#D50000", color: "white" }}>⚠ EXCEDE LMP</span>
                  : <span className="badge" style={{ background: "#00C853", color: "white" }}>✓ Dentro del límite</span>}
              </div>
            </div>
            <div><label className="label">Norma aplicable</label><input className="input" value={env.norma_aplicable_env || ""} onChange={(e) => setEnv({ norma_aplicable_env: e.target.value })} /></div>
            <div><label className="label">Fecha último monitoreo</label><input className="input" type="date" value={env.fecha_ultimo_monitoreo ? String(env.fecha_ultimo_monitoreo).slice(0, 10) : ""} onChange={(e) => setEnv({ fecha_ultimo_monitoreo: e.target.value })} /></div>
          </div>
        </Section>
      )}

      {includesSO && (
        <Section title="Salud Ocupacional" defaultOpen testId="map-form-sect-so">
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">Agente de exposición</label>
              <select className="select" value={health.agente_exposicion || ""} onChange={(e) => setHealth({ agente_exposicion: e.target.value })}>
                <option value="">--</option>{AGENTES_EXP.map((a) => <option key={a}>{a}</option>)}
              </select>
            </div>
            <div><label className="label">Norma aplicable</label>
              <select className="select" value={health.norma_aplicable_so || ""} onChange={(e) => setHealth({ norma_aplicable_so: e.target.value })}>
                <option value="">--</option>{NORMAS_SO.map((n) => <option key={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: "span 2" }}><label className="label">Descripción del agente</label><textarea className="textarea" rows={2} value={health.descripcion_agente || ""} onChange={(e) => setHealth({ descripcion_agente: e.target.value })} /></div>
            <div><label className="label">Límite de exposición</label><input className="input" type="number" value={health.limite_exposicion_normativo ?? ""} onChange={(e) => setHealth({ limite_exposicion_normativo: e.target.value === "" ? "" : parseFloat(e.target.value) })} data-testid="area-so-lim" /></div>
            <div><label className="label">Unidad</label><input className="input" value={health.unidad_exposicion || ""} onChange={(e) => setHealth({ unidad_exposicion: e.target.value })} /></div>
            <div><label className="label">Nivel actual</label><input className="input" type="number" value={health.nivel_exposicion_actual ?? ""} onChange={(e) => setHealth({ nivel_exposicion_actual: e.target.value === "" ? "" : parseFloat(e.target.value) })} data-testid="area-so-val" /></div>
            <div>
              <label className="label">Indicador exposición</label>
              <div data-testid="area-so-badge">
                {health.nivel_exposicion_actual == null || health.limite_exposicion_normativo == null || health.limite_exposicion_normativo === ""
                  ? <span className="badge" style={{ background: "var(--bg-3)" }}>Sin dato</span>
                  : excede_exp_calc
                  ? <span className="badge" style={{ background: "#D50000", color: "white" }}>⚠ EXCEDE</span>
                  : <span className="badge" style={{ background: "#00C853", color: "white" }}>✓ Dentro del límite</span>}
              </div>
            </div>
            <div style={{ gridColumn: "span 2" }}><label className="label">EPP requerido (separar con coma)</label>
              <input className="input" value={(health.epp_requerido || []).join(", ")} onChange={(e) => setHealth({ epp_requerido: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </div>
            <div><label className="label">Vigilancia médica requerida</label>
              <select className="select" value={health.vigilancia_medica_requerida ? "Si" : "No"} onChange={(e) => setHealth({ vigilancia_medica_requerida: e.target.value === "Si" })}>
                <option>No</option><option>Si</option>
              </select>
            </div>
            {health.vigilancia_medica_requerida && (
              <div><label className="label">Frecuencia vigilancia</label>
                <select className="select" value={health.frecuencia_vigilancia || ""} onChange={(e) => setHealth({ frecuencia_vigilancia: e.target.value })}>
                  <option value="">--</option>{FREC_VIG.map((f) => <option key={f}>{f}</option>)}
                </select>
              </div>
            )}
          </div>
        </Section>
      )}

      {includesManual && (
        <Section title="Campos Personalizados" defaultOpen testId="map-form-sect-custom">
          {(form.custom_fields || []).map((cf, i) => (
            <div key={i} className="grid grid-cols-12 gap-1 mb-1">
              <input className="input" style={{ gridColumn: "span 4" }} placeholder="Nombre" value={cf.campo_nombre} onChange={(e) => {
                const next = [...form.custom_fields]; next[i] = { ...cf, campo_nombre: e.target.value }; setF({ custom_fields: next });
              }} />
              <input className="input" style={{ gridColumn: "span 5" }} placeholder="Valor" value={cf.campo_valor} onChange={(e) => {
                const next = [...form.custom_fields]; next[i] = { ...cf, campo_valor: e.target.value }; setF({ custom_fields: next });
              }} />
              <select className="select" style={{ gridColumn: "span 2" }} value={cf.campo_tipo} onChange={(e) => {
                const next = [...form.custom_fields]; next[i] = { ...cf, campo_tipo: e.target.value }; setF({ custom_fields: next });
              }}>
                <option value="texto">Texto</option><option value="numero">Número</option>
                <option value="fecha">Fecha</option><option value="booleano">Sí/No</option>
              </select>
              <button className="btn btn-sm btn-danger" style={{ gridColumn: "span 1" }} type="button" onClick={() => removeCustomField(i)}><X size={10} /></button>
            </div>
          ))}
          {(form.custom_fields || []).length < 5 && (
            <button className="btn btn-sm" type="button" onClick={addCustomField} data-testid="area-custom-add"><Plus size={10} /> Agregar campo</button>
          )}
        </Section>
      )}

      <Section title="Controles y Notas" testId="map-form-sect-ctrl">
        <label className="label">Controles existentes</label>
        <textarea className="textarea" rows={2} value={form.controls || ""} onChange={(e) => setF({ controls: e.target.value })} />
        <label className="label mt-2">Justificación / Notas</label>
        <textarea className="textarea" rows={2} value={form.notes || ""} onChange={(e) => setF({ notes: e.target.value })} />
      </Section>

      <div className="flex justify-end gap-2 mt-3">
        <button className="btn" onClick={onCancel} disabled={saving}>Cancelar</button>
        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="area-save"><Plus size={12} /> {initial ? "Actualizar" : "Agregar"}</button>
      </div>
    </div>
  );
}
